import { Lock } from '@mui/icons-material'
import { Box, Chip, Stack } from '@mui/material'

import type React from 'react'
import { memo } from 'react'

import type { MenuItem } from '../../domain/menu-item'
import { ImageBadge } from './image-badge'
import { MenuItemImage } from './menu-item-image'
import { PopularBadge } from './popular-badge'

interface CardImageProps {
  item: MenuItem
  account?: { name: string }
  t: (key: string, options?: Record<string, unknown>) => string
  numberInCol: number
  /** Renders the tenant-coloured "Popular" badge overlay on the image. */
  popular?: boolean
}

export const CardImage: React.FC<CardImageProps> = memo(
  ({ item, account, t, numberInCol, popular = false }) => {
    const isCompact = numberInCol > 1
    const imageSize = isCompact ? 180 : 280

    return (
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: `${imageSize}px`,

          '@media (max-width: 600px)': {
            width: '100%',
            height: '320px',
          },
        }}>
        <Stack
          alignItems="flex-start"
          spacing={0.5}
          sx={{ position: 'absolute', top: 4, left: 4, right: 4, zIndex: 3 }}>
          {popular && <PopularBadge itemId={item._id} />}
          {item.isPrivate && (
            <ImageBadge
              icon={<Lock fontSize="small" />}
              label={t('marketplace:private', { accountName: account?.name })}
            />
          )}
        </Stack>
        <MenuItemImage
          src={item.image}
          alt={item.name || ''}
          sizes="(max-width: 600px) 100vw, (max-width: 1200px) 280px, 180px"
        />
        {item.options.length > 0 && (
          <Chip
            sx={{
              position: 'absolute',
              right: 12,
              left: 12,
              bottom: 8,
              backgroundColor: 'primary.light',
              zIndex: 2,
              fontSize: '0.75rem',
            }}
            color="primary"
            size="small"
            label={t('marketplace:optionsAvailable')}
          />
        )}
      </Box>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.item._id === nextProps.item._id &&
      prevProps.item.image === nextProps.item.image &&
      prevProps.numberInCol === nextProps.numberInCol &&
      prevProps.popular === nextProps.popular
    )
  },
)

CardImage.displayName = 'CardImage'
